import { useState } from 'react';
import { taskApi, type Task } from '../../api';
import Select from './Select';

type Props = {
  task: Task;
  onClose: () => void;
  onSaved: () => void;
};

const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

export default function EditTaskModal({ task, onClose, onSaved }: Props) {
  const [name, setName] = useState(task.name || '');
  const [description, setDescription] = useState(task.description || '');
  const [priority, setPriority] = useState<Task['priority']>(task.priority);
  const [dueDate, setDueDate] = useState(task.due_date || '');
  const [billable, setBillable] = useState<boolean>(
    task.billable === true || task.billable === 'true'
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError(null);
    try {
      await taskApi.update(task.ROWID, {
        name: name.trim(),
        description: description || null,
        priority,
        due_date: dueDate || null,
        billable,
      });
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>Edit task</h3>
        <div className="stack">
          <div>
            <label>Name</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label>Description</label>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="row">
            <div className="grow">
              <label>Priority</label>
              <Select
                value={priority}
                onChange={(v) => setPriority(v as Task['priority'])}
                options={PRIORITIES.map((p) => ({ value: p, label: p }))}
              />
            </div>
            <div className="grow">
              <label>Due date</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 0, fontSize: 13, color: 'var(--color-text)' }}>
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
              style={{ width: 'auto' }}
            />
            <span>Billable</span>
          </label>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 520, maxWidth: '90vw' };
